import React, { useState, useEffect } from "react";
import "./SchedulerWidget.css";
import fetchBase from "../utils/fetchBase";
import LoadingOverlay from "./LoadingOverlay";

const SchedulerWidget = ({ selectedDate, onClose, refreshEvents }) => {
    const [mode, setMode] = useState("employee");
    const [employees, setEmployees] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [selectedClient, setSelectedClient] = useState("");
    const [shiftHours, setShiftHours] = useState("");
    const [serviceCode, setServiceCode] = useState("");
    const [startHour, setStartHour] = useState("");
    const [startMinute, setStartMinute] = useState("");
    const [startAmPm, setStartAmPm] = useState("AM");
    const [endTime, setEndTime] = useState("");
    const [endTime24, setEndTime24] = useState("");
    const [remainingHours, setRemainingHours] = useState(null);
    const [hasActivePeriod, setHasActivePeriod] = useState(null);
    const [overlay, setOverlay] = useState(null);
    const [payPeriodWarning, setPayPeriodWarning] = useState(null);
    const [billingRate, setBillingRate] = useState("");
    const [recentRates, setRecentRates] = useState([]);
    const [canOverridePeriod, setCanOverridePeriod] = useState(false);

    const formattedDay = selectedDate ? selectedDate.getDate() : "";

    useEffect(() => {
        const fetchEmployees = async () => {
            const token = localStorage.getItem("jwt_token");
            if (!token) return;
            try {
                const response = await fetch(`${fetchBase}/api/admin/by-admin`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json"
                    }
                });
                const data = await response.json();
                console.log("📦 Employees response:", data);
                if (Array.isArray(data)) {
                    setEmployees(data);
                } else {
                    console.error("Expected an array but got:", data);
                    setEmployees([]);
                }
            } catch (error) {
                console.error("❌ Failed to fetch employees:", error);
                setEmployees([]);
            }
        };
        fetchEmployees();
    }, []);

    useEffect(() => {
        const fetchClients = async () => {
            const token = localStorage.getItem("jwt_token");
            if (!token) return;
            const response = await fetch(`${fetchBase}/api/client/by-admin`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json"
                }
            });
            const data = await response.json();
            if (Array.isArray(data)) {
                setClients(data);
            } else {
                console.error("❌ Expected clients array but got:", data);
                setClients([]);
            }
        };
        fetchClients();
    }, []);

    useEffect(() => {
            const token = localStorage.getItem("jwt_token");
            if (!token) return;
            fetch(`${fetchBase}/api/schedule/recent-rates`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) setRecentRates(data);
                })
                .catch(err => console.error("Failed to fetch recent rates:", err));
        }, []);

        useEffect(() => {
            if (startHour && startMinute && shiftHours) {
            let hour = parseInt(startHour);
            const minute = parseInt(startMinute);
            const duration = parseFloat(shiftHours);
            if (startAmPm === "PM" && hour < 12) hour += 12;
            if (startAmPm === "AM" && hour === 12) hour = 0;
            const start = new Date();
            start.setHours(hour, minute, 0);
            const end = new Date(start.getTime() + duration * 60 * 60 * 1000);
            const uiHour = end.getHours() % 12 || 12;
            const uiMin = end.getMinutes().toString().padStart(2, '0');
            const uiAmPm = end.getHours() >= 12 ? "PM" : "AM";
            setEndTime(`${uiHour}:${uiMin} ${uiAmPm}`);
            const backendHour = end.getHours().toString().padStart(2, '0');
            const backendMin = end.getMinutes().toString().padStart(2, '0');
            setEndTime24(`${backendHour}:${backendMin}:00`);
        } else {
            setEndTime("");
            setEndTime24("");
        }
    }, [startHour, startMinute, startAmPm, shiftHours]);

        useEffect(() => {
                if (!selectedEmployee || !selectedClient) {
                    setPayPeriodWarning(null);
                    setCanOverridePeriod(false);
                    return;
                }
                const check = async () => {
                    try {
                        const res = await fetch(`${fetchBase}/api/pay-period/active`, {
                            headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") }
                        });
                        if (!res.ok) return;
                        const text = await res.text();
                        if (!text) { setPayPeriodWarning(null); setCanOverridePeriod(false); return; }
                        const period = JSON.parse(text);
                        const scheduleDate = new Date(selectedDate);
                        const [startY, startM, startD] = period.startDate.split("-").map(Number);
                        const [endY, endM, endD] = period.endDate.split("-").map(Number);
                        const start = new Date(startY, startM - 1, startD);
                        const end = new Date(endY, endM - 1, endD);
                        scheduleDate.setHours(0, 0, 0, 0);
                        start.setHours(0, 0, 0, 0);
                        end.setHours(0, 0, 0, 0);
                            if (scheduleDate < start || scheduleDate > end) {
                                setPayPeriodWarning(`⚠️ ${scheduleDate.toLocaleDateString()} is outside the active pay period (${period.startDate} to ${period.endDate}). This will copy the client's existing schedule pattern into the next pay period.`);
                                setCanOverridePeriod(true);
                            } else {
                                setPayPeriodWarning(null);
                                setCanOverridePeriod(false);
                            }
                    } catch (err) {
                        console.error("Failed to check pay period:", err);
                    }
                };
                check();
            }, [selectedEmployee, selectedClient]);

            const handleCopyForward = async () => {
                try {
                    setOverlay({ message: "Copying schedule to next pay period...", status: "" });
                    const anchorDate = selectedDate
                        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,"0")}-${String(selectedDate.getDate()).padStart(2,"0")}`
                        : null;
                    const response = await fetch(`${fetchBase}/api/schedule/copy-forward`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + localStorage.getItem("jwt_token"),
                        },
                        body: JSON.stringify({
                            employeeId: selectedEmployee,
                            clientId: selectedClient,
                            anchorDate: anchorDate
                        }),
                    });
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Failed to copy forward:", errorText);
                    setOverlay(null);
                    alert(errorText || "Failed to copy schedule to next pay period.");
                } else {
                    setOverlay({ message: "✅ Schedule copied to next pay period.", status: "success" });
                    setTimeout(() => {
                        setOverlay(null);
                        refreshEvents?.();
                        onClose();
                    }, 1500);
                }
            } catch (error) {
                console.error("Error copying schedule forward:", error);
            }
        };

        const handleSchedule = async () => {
        const email = localStorage.getItem("admin_email");
        const password = localStorage.getItem("admin_password");
        if (!selectedEmployee || !selectedClient || !startHour || !startMinute || !endTime24 || !shiftHours || !selectedDate) {
                    alert("Please fill out all required fields.");
                    return;
                }

                if (!canOverridePeriod && hasActivePeriod && remainingHours !== null) {
                    const scheduleDate = new Date(selectedDate);
                    const res = await fetch(`${fetchBase}/api/pay-period/active`, {
                        headers: { Authorization: "Bearer " + localStorage.getItem("jwt_token") }
                    });
                        if (res.ok) {
                            const text = await res.text();
                            if (text) {
                                const period = JSON.parse(text);
                                const [startY, startM, startD] = period.startDate.split("-").map(Number);
                                const [endY, endM, endD] = period.endDate.split("-").map(Number);
                                const start = new Date(startY, startM - 1, startD);
                                const end = new Date(endY, endM - 1, endD);
                                scheduleDate.setHours(0, 0, 0, 0);
                            if (scheduleDate < start || scheduleDate > end) {
                                alert(`⚠️ This date is outside the active pay period (${period.startDate} to ${period.endDate}).`);
                                return;
                            }
                        }
                    }
                }
        const startTime24 = (() => {
            let hour = parseInt(startHour);
            const minute = startMinute.padStart(2, '0');
            if (startAmPm === "PM" && hour < 12) hour += 12;
            if (startAmPm === "AM" && hour === 12) hour = 0;
            return `${hour.toString().padStart(2, '0')}:${minute}:00`;
        })();
        try {
            setOverlay({ message: "Saving schedule...", status: "" });
            const response = await fetch(`${fetchBase}/api/schedule/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("jwt_token"),
                },
                body: JSON.stringify({
                    employeeId: selectedEmployee,
                    clientId: selectedClient,
                    clientColor: clients.find(c => c.id === parseInt(selectedClient))?.color,
                    date: selectedDate,
                    startTime: startTime24,
                    endTime: endTime24,
                    shiftHours: parseFloat(shiftHours),
                    serviceCode: serviceCode,
                    billingRate: billingRate !== "" ? parseFloat(billingRate) : null
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Failed to save schedule:", errorText);
                setOverlay({ message: "❌ Failed to save schedule.", status: "error" });
                setTimeout(() => setOverlay(null), 2500);
            } else {
                console.log("Schedule saved successfully.");
                setOverlay({ message: "✅ Schedule saved successfully.", status: "success" });
                setTimeout(() => {
                    setOverlay(null);
                    refreshEvents?.();
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error("Error submitting schedule:", error);
        }
    };

    return (
            <>
            {overlay && <LoadingOverlay message={overlay.message} status={overlay.status} />}
            <div className={`scheduler-widget ${mode === "event" ? "expanded" : ""} ${mode === "employee" ? "employee-mode" : ""}`}>
            <div className="widget-header">
                <div className="toggle-switch">
                    <button className={`toggle-option ${mode === "employee" ? "active" : ""}`} onClick={() => setMode("employee")}>Employee</button>
                    <button className={`toggle-option ${mode === "event" ? "active" : ""}`} onClick={() => setMode("event")}>Event</button>
                </div>
                <div className="date-badge">{formattedDay}</div>
                <button className="close-button" onClick={onClose}>×</button>
            </div>

            {mode === "employee" && (
                <>
                    <label>Employee</label>
                    <select className="input" value={selectedEmployee}
                            onChange={(e) => setSelectedEmployee(e.target.value)}>
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                        ))}
                    </select>

                    <label>Client</label>
                    <select className="input" value={selectedClient}
                            onChange={async (e) => {
                                const clientId = e.target.value;
                                setSelectedClient(clientId);
                                setRemainingHours(null);
                                setHasActivePeriod(null);
                                if (!clientId) return;
                                const selectedClientObj = clients.find(c => c.id === parseInt(clientId));
                                if (!selectedClientObj || selectedClientObj.clientType !== "VA") return;
                                    try {
                                        const dateParam = selectedDate ? selectedDate.toISOString().split("T")[0] : "";
                                        const res = await fetch(`${fetchBase}/api/schedule/remaining-hours/${clientId}?date=${dateParam}`, {
                                            headers: {
                                                Authorization: "Bearer " + localStorage.getItem("jwt_token")
                                            }
                                        });
                                    if (res.ok) {
                                        const data = await res.json();
                                        console.log("🕐 Remaining hours response:", data);
                                        setRemainingHours(data.remainingHours);
                                        setHasActivePeriod(data.hasActivePeriod === true);
                                    }
                                } catch (err) {
                                    console.error("❌ Failed to fetch remaining hours:", err);
                                }
                            }}>
                        <option value="">Select Client</option>
                        {clients.map((client) => (
                            <option key={client.id} value={client.id}>{client.firstName} {client.lastName}</option>
                        ))}
                    </select>
                    {remainingHours !== null && (
                        <div style={{ margin: "4px 0 0", display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <p style={{ fontSize: "12px", color: !hasActivePeriod ? "orange" : remainingHours <= 0 ? "red" : "#888", margin: "0" }}>
                                {!hasActivePeriod
                                    ? "⚠️ You must start a pay period before scheduling."
                                    : remainingHours <= 0
                                        ? "⚠️ No hours remaining for this client this pay period."
                                        : `${remainingHours}h remaining this pay period`}
                            </p>
                            {!hasActivePeriod && (
                                <button
                                    onClick={() => window.location.href = "/admin/payroll?tab=live"}
                                    style={{
                                        fontSize: "12px",
                                        color: "#fff",
                                        backgroundColor: "#1d1d1f",
                                        padding: "4px 10px",
                                        borderRadius: "4px",
                                        border: "none",
                                        cursor: "pointer",
                                        marginTop: "4px"
                                    }}
                                >
                                    Go to Payroll →
                                </button>
                            )}
                        </div>
                    )}

                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <div style={{ flex: 2 }}>
                            <label>Start time</label>
                            <div className="horizontal-time-inputs">
                                <select className="input small" value={startHour}
                                        onChange={(e) => setStartHour(e.target.value)}>
                                    <option value="">Hr</option>
                                    {[...Array(12)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                                    ))}
                                </select>
                                <select className="input small" value={startMinute}
                                        onChange={(e) => setStartMinute(e.target.value)}>
                                    <option value="">Min</option>
                                    {["00", "15", "30", "45"].map(min => (
                                        <option key={min} value={min}>{min}</option>
                                    ))}
                                </select>
                                <select className="input small" value={startAmPm}
                                        onChange={(e) => setStartAmPm(e.target.value)}>
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Duration</label>
                            <input
                                className="input"
                                type="number"
                                placeholder="hrs"
                                value={shiftHours}
                                max={remainingHours !== null && remainingHours >= 0 ? remainingHours : undefined}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    if (remainingHours !== null && remainingHours >= 0 && val > remainingHours) {
                                        setShiftHours(remainingHours.toString());
                                    } else {
                                        setShiftHours(e.target.value);
                                    }
                                }}
                            />

                        </div>
                    </div>

                    <label>Service Code</label>
                    <select
                        className="input"
                        value={serviceCode}
                        onChange={(e) => setServiceCode(e.target.value)}
                    >
                        <option value="">Select Code</option>
                        <option value="HHA *G0156*">HHA *G0156*</option>
                        <option value="Respite *S5150*">Respite *S5150*</option>
                        <option value="RN VISIT*G0162*">RN VISIT*G0162*</option>
                    </select>

                    <label>Billing Rate</label>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <input
                                className="input"
                                type="number"
                                placeholder="e.g. 38.00"
                                value={billingRate}
                                onChange={(e) => setBillingRate(e.target.value)}
                                style={{ flex: 2 }}
                            />
                            {recentRates.length > 0 && (
                                <select
                                    className="input"
                                    style={{ flex: 1 }}
                                    onChange={(e) => setBillingRate(e.target.value)}
                                    defaultValue=""
                                >
                                    <option value="" disabled>Recent</option>
                                    {recentRates.map((rate, idx) => (
                                        <option key={idx} value={rate}>{rate}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <label>End time</label>
                        <input className="input" type="text" value={endTime} disabled/>
                </>
            )}

            {mode === "event" && (
                <>
                    <label>Title</label>
                    <input className="input" type="text" placeholder="Enter event title"/>
                    <label>Start Date & Time</label>
                    <div className="time-row">
                        <input className="input" type="date" />
                        <input className="input" type="time" />
                    </div>
                    <label>End Date & Time</label>
                    <div className="time-row">
                        <input className="input" type="date" />
                        <input className="input" type="time" />
                    </div>
                    <label>Notes</label>
                    <textarea className="input" placeholder="Enter notes..." rows="3" />
                </>
            )}

                {payPeriodWarning && (
                    <p style={{ fontSize: "12px", color: "orange", margin: "4px 0 8px" }}>{payPeriodWarning}</p>
                )}
                {canOverridePeriod ? (
                    <>
                        <button
                            className="schedule-button"
                            onClick={handleCopyForward}
                        >
                            Copy to Next Pay Period
                        </button>
                        <button
                            className="schedule-button"
                            onClick={handleSchedule}
                            style={{ marginTop: "8px", backgroundColor: "#f5f5f7", color: "#1d1d1f" }}
                        >
                            Continue Scheduling
                        </button>
                    </>
                ) : (
                    <button
                        className="schedule-button"
                        onClick={handleSchedule}
                        disabled={!!payPeriodWarning}
                        style={payPeriodWarning ? { backgroundColor: "#ccc", cursor: "not-allowed", opacity: 0.6 } : {}}
                    >
                        Schedule
                    </button>
                )}

            {mode === "employee" && (
                <p className="note">
                    For VA: use hours remaining to avoid over-scheduling. Some clients may not have minimum hours.
                </p>
            )}
            </div>
        </>
    );
};

export default SchedulerWidget;